"use client";

import { usePathname } from "next/navigation";
import { useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/lib/analytics/hooks";
import {
	type SubscribeLandingRoleAlertResult,
	subscribeLandingRoleAlert,
} from "@/server/actions/landing-role-alert";

type Props = {
	roleTitle: string;
	roleSlug: string;
	locale: "id" | "en";
	isId: boolean;
	/**
	 * Where this form is embedded, carried into
	 * `landing_role_alert_subscribed.trigger_surface`. It used to be hardcoded
	 * to the landing module, which was the only caller; a second caller with the
	 * same label would make the two placements unsplittable in PostHog.
	 */
	triggerSurface?: string;
};

/**
 * Minimal anonymous alert-subscribe input for the landing→product bridge
 * module (P1-6). Persists intent via `subscribeLandingRoleAlert` — the seam
 * for P1-5's real (signed-in, `saved_searches`-backed) alert engine, see
 * that server action's doc comment.
 */
export function LandingAlertSubscribeForm({
	roleTitle,
	roleSlug,
	locale,
	isId,
	triggerSurface = "landing_jobs_module",
}: Props) {
	const pathname = usePathname();
	const { posthog } = useAnalytics();
	const [email, setEmail] = useState("");
	const [result, setResult] = useState<SubscribeLandingRoleAlertResult | null>(
		null,
	);
	const [pending, startTransition] = useTransition();

	function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setResult(null);
		startTransition(async () => {
			const res = await subscribeLandingRoleAlert({
				email,
				roleTitle,
				roleSlug,
				locale,
				sourceUrl: pathname ?? undefined,
			});
			setResult(res);
			if (res.ok) {
				posthog.capture("landing_role_alert_subscribed", {
					trigger_surface: triggerSurface,
					role_slug: roleSlug,
					already_subscribed: res.alreadySubscribed,
				});
			}
		});
	}

	if (result?.ok) {
		return (
			<p
				className="text-[13px] text-muted-foreground"
				data-testid="landing-alert-subscribed"
			>
				{isId
					? `Beres — kami akan mengabarimu saat ada lowongan ${roleTitle} baru.`
					: `You're set — we'll email you when new ${roleTitle} jobs go live.`}
			</p>
		);
	}

	return (
		<form
			onSubmit={onSubmit}
			className="flex flex-col gap-2 sm:flex-row sm:items-start"
			data-testid="landing-alert-subscribe-form"
		>
			<div className="flex-1">
				<label htmlFor="landing-alert-email" className="sr-only">
					{isId ? "Alamat email" : "Email address"}
				</label>
				<input
					id="landing-alert-email"
					type="email"
					required
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					placeholder={isId ? "email@kamu.com" : "you@work.com"}
					autoComplete="email"
					className="w-full rounded-[980px] border border-border bg-background px-4 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				/>
				{result && !result.ok && (
					<p className="mt-1.5 text-[12px] text-destructive" role="alert">
						{result.error}
					</p>
				)}
			</div>
			<Button
				type="submit"
				disabled={pending}
				shape="pill"
				size="sm"
				className="shrink-0 text-[13px]"
			>
				{pending
					? isId
						? "Mengirim…"
						: "Sending…"
					: isId
						? "Kirimi saya update"
						: "Get job alerts"}
			</Button>
		</form>
	);
}
